/**
 * Public-mempool/chain probe for Zcash txids.
 *
 * Used by the Wallet's "Recover balance" flow: after we rebuild local
 * shielded state from on-chain truth, any tx still flagged "pending"
 * in our local tx-history can be checked against a public ZEC indexer
 * to know whether it ever made it into mempool. If the indexer has
 * never seen the txid, the relay must have accepted but failed to
 * propagate — we can flip the tx to "failed" instead of leaving the
 * user staring at a forever-spinner.
 *
 * Blockchair's public API allows CORS for browsers and rate-limits
 * generously (10 req/min unsleeve). One probe per pending tx after a
 * user-initiated recovery is well below that.
 */

/**
 * Returns:
 *   "confirmed" — txid is on chain in a finalised block.
 *   "mempool"   — txid is in mempool (broadcast worked, just slow).
 *   "missing"   — chain has never seen this txid (relay drop / wallet bug).
 *   "unknown"   — the probe itself failed (network error, indexer down).
 *
 * The caller only acts decisively on "missing" — "unknown" is treated
 * as "still trying", so an intermittent Blockchair outage can't move
 * a healthy tx to failed.
 */
export type ChainTxStatus = "confirmed" | "mempool" | "missing" | "unknown";

const BLOCKCHAIR_BASE = "https://api.blockchair.com/zcash";

interface DashboardResponse {
  data?: Record<string, { transaction?: { block_id?: number } }>;
}

interface MempoolResponse {
  data?: Array<{ hash?: string }>;
}

export async function checkZcashTxStatus(
  txid: string,
  options: { signal?: AbortSignal } = {},
): Promise<ChainTxStatus> {
  const id = txid.toLowerCase();
  // 1) Dashboard endpoint returns `data: { <txid>: {…block_id…} }` for
  //    confirmed transactions and either an empty object OR (observed
  //    in the wild) a literal 200 with empty body for unknown ones.
  //    Both empty shapes fall through to the mempool check.
  try {
    const r = await fetch(`${BLOCKCHAIR_BASE}/dashboards/transaction/${id}`, {
      signal: options.signal,
    });
    if (r.ok) {
      const body = await r.text();
      if (body.length > 0) {
        try {
          const j = JSON.parse(body) as DashboardResponse;
          const data = j.data ?? {};
          const entry = data[id];
          if (entry?.transaction?.block_id) return "confirmed";
          // Empty `data` ↔ not on chain; proceed to mempool check.
        } catch {
          // Non-JSON body (Blockchair sometimes serves an HTML "you
          // hit our rate limit" page) — treat as transient.
          return "unknown";
        }
      }
      // Empty body — Blockchair's response for an unknown txid in this
      // path. Fall through to the mempool check before concluding.
    } else if (r.status !== 404 && r.status !== 400) {
      // 5xx / rate-limit — indexer hiccup; bail to unknown.
      return "unknown";
    }
  } catch {
    return "unknown";
  }

  // 2) Cross-check the mempool — covers the narrow window where a real
  //    tx is broadcast and seen by Blockchair's mempool watcher but not
  //    yet mined. Without this we'd false-positive a fresh broadcast as
  //    "missing".
  try {
    const r = await fetch(`${BLOCKCHAIR_BASE}/mempool/transactions?limit=10000`, {
      signal: options.signal,
    });
    if (r.ok) {
      const j = (await r.json()) as MempoolResponse;
      const inMempool = (j.data ?? []).some(
        (entry) => entry.hash?.toLowerCase() === id,
      );
      if (inMempool) return "mempool";
    }
  } catch {
    // ignore — proceed to "missing"
  }

  return "missing";
}

/**
 * Bulk variant — probes a list of txids in parallel with a small
 * concurrency limit so we don't hammer the indexer when a user has
 * accumulated several stuck rows in their history.
 */
export async function checkZcashTxStatusBatch(
  txids: string[],
  options: { signal?: AbortSignal } = {},
): Promise<Map<string, ChainTxStatus>> {
  const out = new Map<string, ChainTxStatus>();
  if (txids.length === 0) return out;
  // Three probes in flight at a time — enough for snappy UI feedback,
  // light enough that Blockchair's free tier won't rate-limit on a
  // recovery flow.
  const concurrency = 3;
  let cursor = 0;
  const workers = Array.from({ length: Math.min(concurrency, txids.length) }).map(
    async () => {
      while (cursor < txids.length) {
        const idx = cursor++;
        const id = txids[idx]!;
        out.set(id, await checkZcashTxStatus(id, { signal: options.signal }));
      }
    },
  );
  await Promise.all(workers);
  return out;
}
